import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryError } from 'src/queries/network.utils'
import {
  getOrgExpenseReportReceiptReminderStatus,
  getOrgReceiptReminderStatus,
  postOrgExpenseReportReceiptReminder,
  postOrgReceiptReminder,
} from 'src/queries/organizations/receiptReminders/receiptReminders.network'
import { ORG_EXPENSE_REPORTS_QK, ORG_QK } from 'src/queries/queries.constants'
import { CustomMutationOptions } from 'src/queries/queries.utils'

/**
 * Composed from the shared org roots so send mutations invalidate the exact status entry a read caches; colocated since no cross-feature site targets it.
 */
const RECEIPT_REMINDER_STATUS = 'receiptreminderstatus'

export const receiptReminderQueryKeys = {
  org: (orgId: string | undefined) =>
    [ORG_QK, orgId, RECEIPT_REMINDER_STATUS] as const,
  orgExpenseReport: (reportId: string | undefined) =>
    [ORG_EXPENSE_REPORTS_QK, reportId, RECEIPT_REMINDER_STATUS] as const,
}

// No staleTime override: the app QueryClient defaults refetch the status each
// time the popup remounts.
export const receiptReminderQueries = {
  orgStatus: (orgId: string | undefined) =>
    queryOptions({
      queryKey: receiptReminderQueryKeys.org(orgId),
      queryFn: () => getOrgReceiptReminderStatus(orgId!),
      enabled: Boolean(orgId),
    }),
  orgExpenseReportStatus: (reportId: string | undefined) =>
    queryOptions({
      queryKey: receiptReminderQueryKeys.orgExpenseReport(reportId),
      queryFn: () => getOrgExpenseReportReceiptReminderStatus(reportId!),
      enabled: Boolean(reportId),
    }),
}

export const receiptReminderMutations = {
  sendOrg: () =>
    mutationOptions<void, QueryError, string>({
      mutationFn: (orgId) => postOrgReceiptReminder(orgId),
    }),
  sendOrgExpenseReport: () =>
    mutationOptions<void, QueryError, string>({
      mutationFn: (reportId) => postOrgExpenseReportReceiptReminder(reportId),
    }),
}

export function useSendOrgReceiptReminder(
  options?: CustomMutationOptions<void, QueryError, string>,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    ...receiptReminderMutations.sendOrg(),
    onSuccess: (data, orgId, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: receiptReminderQueryKeys.org(orgId),
      })
      onSuccess?.(data, orgId, onMutateResult, context)
    },
    ...restOptions,
  })
}

export function useSendOrgExpenseReportReceiptReminder(
  options?: CustomMutationOptions<void, QueryError, string>,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    ...receiptReminderMutations.sendOrgExpenseReport(),
    onSuccess: (data, reportId, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: receiptReminderQueryKeys.orgExpenseReport(reportId),
      })
      onSuccess?.(data, reportId, onMutateResult, context)
    },
    ...restOptions,
  })
}
